import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertyEditWebApiHandler_hasAttachedGarageParking,
  useRentalManagerUserPropertyEditWebApiHandler_hasCoveredParkingParking,
  useRentalManagerUserPropertyEditWebApiHandler_hasDetachedGarageParking,
  useRentalManagerUserPropertyEditWebApiHandler_hasMultiLevelGarageParking,
  useRentalManagerUserPropertyEditWebApiHandler_hasNoneParking,
  useRentalManagerUserPropertyEditWebApiHandler_hasParkingLotParking,
  useRentalManagerUserPropertyEditWebApiHandler_hasUndergroundGarageParking,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { getIsInputFormDisabled } from '../../utils';

import { DEFAULT_ERROR_MESSAGE } from '../../../../constants/ErrorMessageConstants';
import {
  PARKING_TYPE_METADATA,
  PARKING_TYPE_TYPE_KEYS,
  type ParkingTypeKey,
} from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<ParkingTypeKey, boolean>;

const NONE_KEY: ParkingTypeKey = 'hasNoneParking';

const ParkingTypeAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const parkingTypes = listingDetails?.buildingAttributes?.parkingTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasAttachedGarage: parkingTypes?.hasAttachedGarage ?? false,
    hasCoveredParking: parkingTypes?.hasCoveredParking ?? false,
    hasDetachedGarage: parkingTypes?.hasDetachedGarage ?? false,
    hasParkingLot: parkingTypes?.hasParkingLot ?? false,
    hasMultiLevelGarage: parkingTypes?.hasMultiLevelGarage ?? false,
    hasUndergroundGarage: parkingTypes?.hasUndergroundGarage ?? false,
    hasNoneParking: parkingTypes?.hasNoneParking ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Parking types updated',
  });

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: updateAttachedGarage } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasAttachedGarageParking,
    skipToast: true,
    meta: { context: { api: 'hasAttachedGarageParking', details: { listingAlias } } },
  });

  const { mutateAsync: updateCoveredParking } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasCoveredParkingParking,
    skipToast: true,
    meta: { context: { api: 'hasCoveredParkingParking', details: { listingAlias } } },
  });

  const { mutateAsync: updateDetachedGarage } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasDetachedGarageParking,
    skipToast: true,
    meta: { context: { api: 'hasDetachedGarageParking', details: { listingAlias } } },
  });

  const { mutateAsync: updateParkingLot } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasParkingLotParking,
    skipToast: true,
    meta: { context: { api: 'hasParkingLotParking', details: { listingAlias } } },
  });

  const { mutateAsync: updateMultiLevelGarage } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasMultiLevelGarageParking,
    skipToast: true,
    meta: { context: { api: 'hasMultiLevelGarageParking', details: { listingAlias } } },
  });

  const { mutateAsync: updateUndergroundGarage } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasUndergroundGarageParking,
    skipToast: true,
    meta: { context: { api: 'hasUndergroundGarageParking', details: { listingAlias } } },
  });

  const { mutateAsync: updateNoneParking } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasNoneParking,
    skipToast: true,
    meta: { context: { api: 'hasNoneParking', details: { listingAlias } } },
  });

  const mutationMap: Record<keyof LocalState, typeof updateAttachedGarage> = {
    hasAttachedGarage: updateAttachedGarage,
    hasCoveredParking: updateCoveredParking,
    hasDetachedGarage: updateDetachedGarage,
    hasParkingLot: updateParkingLot,
    hasMultiLevelGarage: updateMultiLevelGarage,
    hasUndergroundGarage: updateUndergroundGarage,
    hasNoneParking: updateNoneParking,
  };

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as ParkingTypeKey;
    const checked = event.target.value === 'true';

    startRequest();

    // Special case: clicking "None" should deselect all others
    if (fieldName === NONE_KEY && checked) {
      const previousState = { ...localState };
      const newState = PARKING_TYPE_TYPE_KEYS.reduce(
        (acc, key) => ({ ...acc, [key]: key === NONE_KEY }),
        {} as Record<ParkingTypeKey, boolean>,
      );
      setLocalState(newState);

      try {
        // Set "None" to true
        await mutationMap[NONE_KEY]({
          getParams: { [PARKING_TYPE_METADATA[NONE_KEY].apiKey]: true, listingAlias },
        });

        // Set all others to false
        await Promise.all(
          PARKING_TYPE_TYPE_KEYS.filter((k) => k !== NONE_KEY).map((key) =>
            mutationMap[key]({
              getParams: { [PARKING_TYPE_METADATA[key].apiKey]: false, listingAlias },
            }),
          ),
        );

        handleSuccess();
      } catch (err) {
        handleError();
        setLocalState(previousState);
        const errorMessage = DatarouterError.getDisplayMessage(err, DEFAULT_ERROR_MESSAGE);
        enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
      }
      return;
    }

    // Special case: clicking any other option should deselect "None"
    const previousValue = !checked;
    const previousNoneValue = localState.hasNoneParking;
    setLocalState((prev) => ({
      ...prev,
      [fieldName]: checked,
      hasNoneParking: false, // Always deselect "None" when selecting anything else
    }));

    try {
      const metadata = PARKING_TYPE_METADATA[fieldName];
      // Update the clicked field
      await mutationMap[fieldName]({
        getParams: { [metadata.apiKey]: checked, listingAlias },
      });

      // If "None" was previously selected, also update it to false
      if (previousNoneValue) {
        await mutationMap[NONE_KEY]({
          getParams: { [PARKING_TYPE_METADATA[NONE_KEY].apiKey]: false, listingAlias },
        });
      }

      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue, hasNoneParking: previousNoneValue }));
      const errorMessage = DatarouterError.getDisplayMessage(err, DEFAULT_ERROR_MESSAGE);
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: '400', flex: { lg: '1 1 50%', base: 'auto' } }}>
      <FieldSet legend={<Legend>Parking options</Legend>} disabled={isInputFormDisabled}>
        {PARKING_TYPE_TYPE_KEYS.map((fieldName) => {
          const metadata = PARKING_TYPE_METADATA[fieldName];
          return (
            <LabeledControl
              key={`parkingTypes-${metadata.label}`}
              label={<Label>{metadata.label}</Label>}
              control={
                <Checkbox
                  name={fieldName}
                  value={String(!localState[fieldName])}
                  checked={localState[fieldName]}
                  onChange={handleCheckboxChange}
                />
              }
            />
          );
        })}
      </FieldSet>
    </Box>
  );
};

export default memo(ParkingTypeAmenities);
